import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';
import type { SearchSelectItem } from './SearchSelect';

type RouterOutputs = inferRouterOutputs<AppRouter>;

export type GlobalExitCandidateSearchResult = RouterOutputs['tradeLogicExitCandidates']['searchGlobalCandidates']['candidates'][number];
export type ExitCandidateSource = GlobalExitCandidateSearchResult['source'];

const SOURCE_LABELS: Record<ExitCandidateSource, string> = {
    aero: 'AERO',
    base_strategy: 'Base strategy',
    imported: 'Imported',
    manual: 'Manual',
    system_grid: 'System grid'
};

export const normalizeGlobalExitCandidateSearchQuery = (value: string): string => {
    return value.trim();
};

export const toExitCandidateSearchItem = (candidate: GlobalExitCandidateSearchResult): SearchSelectItem => ({
    badge: candidate.status === 'active' ? undefined : candidate.status,
    label: `${candidate.label} (#${candidate.id})`,
    meta: `${SOURCE_LABELS[candidate.source]} · exit version ${candidate.tradeLogicV2ExitVersionId}`,
    name: candidate.label,
    searchText: [
        candidate.candidateHash,
        candidate.exitFingerprint ?? '',
        candidate.candidateKey ?? '',
        candidate.description ?? '',
        candidate.source
    ]
        .join(' ')
        .toLowerCase(),
    value: candidate.id
});

export interface ExitCandidateOptionSetInput {
    /** A bounded, server-vetted allow-list, such as a strategy's attachments. */
    allowedIds: readonly number[] | undefined;
    /** Candidate DTOs the owning surface already loaded. */
    hydratedCandidates: readonly GlobalExitCandidateSearchResult[];
    /** The current page of the global server search. */
    searchCandidates: readonly GlobalExitCandidateSearchResult[];
    /** Candidates fetched by ID so known selections stay readable outside the search page. */
    hydrationCandidates: readonly GlobalExitCandidateSearchResult[];
}

export interface ExitCandidateOptionSet {
    items: SearchSelectItem[];
    /** False when the options are a bounded allow-list the control can filter locally. */
    isServerFiltered: boolean;
}

/**
 * Builds the selectable option set.
 *
 * Without an allow-list the menu mirrors the server search, so hydration-only rows stay out of it. With one, the
 * allow-list itself is the complete option set: the global search returns only the newest page of candidates, so
 * letting it decide selectability silently hides older attachments the owning surface already approved.
 */
export const buildExitCandidateOptions = ({
    allowedIds,
    hydratedCandidates,
    searchCandidates,
    hydrationCandidates
}: ExitCandidateOptionSetInput): ExitCandidateOptionSet => {
    const candidateById = new Map<number, GlobalExitCandidateSearchResult>();

    for (const candidate of hydratedCandidates) candidateById.set(candidate.id, candidate);
    for (const candidate of searchCandidates) candidateById.set(candidate.id, candidate);
    for (const candidate of hydrationCandidates) candidateById.set(candidate.id, candidate);

    if (allowedIds) {
        const items: SearchSelectItem[] = [];

        for (const candidateId of allowedIds) {
            const candidate = candidateById.get(candidateId);
            if (candidate) items.push(toExitCandidateSearchItem(candidate));
        }

        return { items, isServerFiltered: false };
    }

    const searchCandidateIds = new Set(searchCandidates.map((candidate) => candidate.id));

    return {
        items: Array.from(candidateById.values()).map((candidate) => ({
            ...toExitCandidateSearchItem(candidate),
            serverSearchMatch: searchCandidateIds.has(candidate.id)
        })),
        isServerFiltered: true
    };
};
